import { Box } from "@chakra-ui/react";
import { call, colors, customEvent, Spacing } from "goi_common";
import { BaseStack } from "goi_common/src/design-system";
import { useEffect, useState } from "react";
import {
  FAMILY_TRACK_RECORD,
  MUBINSO_PHONE,
  mubinsoLeadStatus,
  MubinsoEntry,
  mubinsoEntryTrigger,
} from "./mubinsoConstants";
import { MubinsoConversionScreen } from "./MubinsoConversionScreen";
import { MubinsoCostChart } from "./MubinsoCostChart";
import { MubinsoLeadModal } from "./MubinsoLeadModal";
import { MubinsoTimeline } from "./MubinsoTimeline";
import { MubinsoCtaButton, MubinsoInfoBox, MubinsoScreenLayout } from "./MubinsoUI";
import { buildMubinsoLeadMessage } from "./mubinsoLeadMessage";

const TOTAL_STEPS = 4;

/**
 * 무빈소 메인 퍼널(풀퍼널): 장례식장 찾기 → 비용 → 마음 → 절차 → 전환.
 * Q1 임종/임박(urgent) 또는 Q2 "세가지 다"로 진입.
 */
export default function MubinsoMainFunnel({
  isUrgent,
  situationAnswer,
  topicAnswer,
  onComplete,
  entry,
  family = false,
}: {
  isUrgent: boolean;
  situationAnswer: string;
  topicAnswer: string;
  onComplete: () => void;
  /** 진입 구분. 퍼널별 개별 이벤트({p}-cta_main 등) 발화에 쓴다 */
  entry: MubinsoEntry;
  /** M5 가족장 경로. 고이 실적 문구를 「장례 5,000건」으로 바꾼다(첫 화면과 같은 숫자) */
  family?: boolean;
}) {
  // 1~4 = 정보 화면, 5 = 전환(리드 제출)
  const [step, setStep] = useState(1);
  const [openModal, setOpenModal] = useState<null | "info" | "consult">(null);

  // 단계 전환 시 스크롤 최상단 + 퍼널별 진행 이벤트.
  useEffect(() => {
    window.scrollTo(0, 0);
    customEvent({
      newGtm: { action: "mubinso_funnel_step", properties: { funnel: "main", step } },
    });
  }, [step]);

  const goNext = () => {
    customEvent({
      newGtm: { path: "mubinso_funnel", trigger_and_target: `funnel-next-${step}` },
    });
    setStep((s) => s + 1);
  };

  if (step === 1) {
    return (
      <MubinsoScreenLayout
        progress={{ current: 1, total: TOTAL_STEPS }}
        contentBottomGap={56}
        footer={<MubinsoCtaButton label="다음" onClick={goNext} />}
      >
        <Box width="100%" whiteSpace="pre-line" className="head2_b black">
          <span style={{ color: colors.orange650 }}>무빈소 장례가 가능한</span>
          <br />
          장례식장부터 찾아야 해요
        </Box>
        <Spacing gap={14} />
        <Box width="100%" whiteSpace="pre-line" className="body_16_r gray600">
          {"무빈소 장례를 받지 않는\n장례식장도 많아요."}
        </Box>
        <Spacing gap={32} />
        <MubinsoInfoBox label="왜 그런가요?">
          무빈소 장례는 장례식장 입장에서는 수익이 크게 발생하지 않는 장례 형태에요.
          <br />
          그래서 진행이 불가능한 장례식장이 많아요.{" "}
          <b style={{ color: colors.gray900 }}>가능한 장례식장을 먼저 확인하는 것</b>이 첫 단계예요.
        </MubinsoInfoBox>
      </MubinsoScreenLayout>
    );
  }

  if (step === 2) {
    return (
      <MubinsoScreenLayout
        progress={{ current: 2, total: TOTAL_STEPS }}
        footer={<MubinsoCtaButton label="다음" onClick={goNext} />}
      >
        <Box width="100%" className="head2_b black">
          비용은 이 정도예요
        </Box>
        <Spacing gap={14} />
        <Box width="100%" whiteSpace="pre-line" className="body_16_r gray600">
          보편적인 장례보다
          <br />
          <b style={{ color: colors.orange650 }}>약 700만원</b> 적게 들어요.
        </Box>
        <Spacing gap={28} />
        <MubinsoCostChart />
        <Spacing gap={16} />
        <Box width="100%" className="body_14_r gray500">
          빈소·음식·제단꽃이 빠지는 만큼 비용이 줄어요.
        </Box>
      </MubinsoScreenLayout>
    );
  }

  if (step === 3) {
    return (
      <MubinsoScreenLayout
        progress={{ current: 3, total: TOTAL_STEPS }}
        contentBottomGap={56}
        footer={<MubinsoCtaButton label="다음" onClick={goNext} />}
      >
        <Box width="100%" whiteSpace="pre-line" className="head2_b black">
          형식은 간소해져도,
          <br />
          <span style={{ color: colors.orange650 }}>마음의 깊이</span>는 다르지 않아요
        </Box>
        <Spacing gap={14} />
        <Box width="100%" whiteSpace="pre-line" className="body_16_r gray600">
          {"가장 많은 분들이\n걱정하시는 부분이에요."}
        </Box>
        <Spacing gap={32} />
        <MubinsoInfoBox variant="peach" label="그래서 중요한 것" labelColor={colors.orange750}>
          다른 절차가 생략된 만큼, 고인을 뵙는 <b style={{ color: colors.gray900 }}>입관식</b>에
          마음을 담아 잘 진행하면 돼요.
        </MubinsoInfoBox>
      </MubinsoScreenLayout>
    );
  }

  if (step === 4) {
    return (
      <MubinsoScreenLayout
        progress={{ current: 4, total: TOTAL_STEPS }}
        footer={<MubinsoCtaButton label="다음" onClick={goNext} />}
      >
        <Box width="100%" whiteSpace="pre-line" className="head2_b black">
          가족분들은 자택에 계시다가,
          <br />
          다음 날 <span style={{ color: colors.orange650 }}>입관식</span>에<br />
          참여하시면 됩니다
        </Box>
        <Spacing gap={32} />
        <MubinsoTimeline
          items={[
            {
              phase: "장례 첫날",
              title: "자택에서 머무르세요",
              desc: "빈소를 지키지 않아도 괜찮아요.",
            },
            {
              phase: "다음 날 · 발인 전",
              title: "입관식에 참여해요",
              desc: "고인을 뵐 수 있는 마지막 자리예요.\n그만큼 가장 중요한 시간이에요.",
              highlight: true,
            },
            { phase: "이어서", title: "발인" },
          ]}
        />
        <Spacing gap={24} />
        <BaseStack width="100%" bg={colors.orange200} borderRadius="16px" p={20} spacing={8}>
          <Box className="body_16_r" color={colors.orange800}>
            그렇기에 입관식을 진행하는{" "}
            <b style={{ color: colors.orange750 }}>장례지도사의 정성과 세심함</b>이 무엇보다
            중요해요.
          </Box>
          <Box className="body_14_r gray600">
            장례식장 소속 지도사는 여러 빈소를 함께 담당해, 이 정도의 세심함을 기대하기 어려울 수
            있어요.
          </Box>
        </BaseStack>
      </MubinsoScreenLayout>
    );
  }

  // step 5 - 전환
  return (
    <>
      <MubinsoConversionScreen
        entry={entry}
        headline={
          <>
            지금 장례 준비가 필요하다면,
            <br />
            고이가 <span style={{ color: colors.orange650 }}>무료로</span> 빠르게 안내드릴게요
          </>
        }
        sub="필요하시다면 진행까지 맡아서 도와드려요."
        checkItems={[
          { text: "지금 이용 가능한 전국 무빈소 장례식장" },
          { text: "실제 예상 비용" },
          { text: "요청 시 예약·이송·입관식까지 진행 지원", underline: true },
        ]}
        checkSpacing={6}
        reviewTitle="실제 진행한 가족분들의 후기"
        reviews={[
          {
            text: "“무빈소 장례를 처음 치러봤는데 조금도 모자람 없이 고인을 예우해 잘 도와주셨어요. 다른 분들께도 고이상조 추천드려요.”",
            meta: "2026. 05. 31 작성 후기 · 황OO 고객님",
          },
          {
            text: "“입관식에 들어간 가족 모두 놀랐어요. ‘무빈소여도 가시는 길은 정말 평온하시겠구나’ 싶었습니다.”",
            meta: "2026. 07. 09 작성 후기 · 김OO 고객님",
          },
        ]}
        showPhoneLink
        onPhoneClick={() => {
          customEvent({ newGtm: { path: "mubinso_funnel", trigger_and_target: "conv-call" } });
          call(MUBINSO_PHONE);
        }}
        primaryLabel="무료로 더 알아보기"
        onPrimary={() => {
          customEvent({ newGtm: { path: "mubinso_funnel", trigger_and_target: "conv-info_cta" } });
          customEvent({
            newGtm: {
              path: "mubinso_funnel",
              trigger_and_target: mubinsoEntryTrigger(entry, "cta_main"),
            },
          });
          setOpenModal("info");
        }}
        secondaryLabel="무료 상담 신청하기"
        onSecondary={() => {
          customEvent({ newGtm: { path: "mubinso_funnel", trigger_and_target: "conv-cnslt_cta" } });
          setOpenModal("consult");
        }}
      />

      <MubinsoLeadModal
        isOpen={openModal === "info"}
        onClose={() => setOpenModal(null)}
        title="지금 필요한 정보 받아보기"
        freeTag
        description={
          family
            ? `${FAMILY_TRACK_RECORD}의 경험을 바탕으로,\n지금 필요하신 내용을 빠르게 전해드려요.`
            : "1,000건 이상의 무빈소 장례 경험을 바탕으로,\n지금 필요하신 내용을 빠르게 전해드려요."
        }
        buttonText="동의하고 자료 받아보기"
        messageBuilder={() =>
          buildMubinsoLeadMessage({
            ctaType: "info",
            situation: situationAnswer,
            topic: topicAnswer,
          })
        }
        leadStatus={mubinsoLeadStatus(situationAnswer)}
        entry={entry}
        onSubmitted={onComplete}
      />
      <MubinsoLeadModal
        isOpen={openModal === "consult"}
        onClose={() => setOpenModal(null)}
        title="무빈소 장례 무료 상담받기"
        description={
          "진행 가능한 장례식장과 예상 비용, 절차를\n빠르게 안내해드려요.\n\n정보만 받아가셔도 괜찮아요.\n준비하시는 데 최대한 도움드릴게요."
        }
        urgentNote={isUrgent}
        buttonText="지금 무료 상담받기"
        messageBuilder={() =>
          buildMubinsoLeadMessage({
            ctaType: "consult",
            situation: situationAnswer,
            topic: topicAnswer,
          })
        }
        leadStatus={mubinsoLeadStatus(situationAnswer)}
        entry={entry}
        onSubmitted={onComplete}
      />
    </>
  );
}
